import { applyClockEvent } from "./clock";
import type { BreakQualification, ClockEventKind, ClockState } from "./clock";
import { failure, success } from "./result";
import type { Result } from "./result";
import { toLocalDate } from "./time";
import type { EpochSeconds, LocalDate } from "./time";

/*
 * Working day projection (spec 6.1.3, ADR-0007), identical to `WorkDayProjection` in the backend
 * and verified with the shared cases in `shared-test-cases/workday-projection.json`.
 */

/** A clock event as the projection needs it. */
export interface ClockEvent {
  /** Event identifier (UUIDv7, lowercase canonical form). */
  readonly id: string;
  /** Event kind. */
  readonly kind: ClockEventKind;
  /** Instant of the event (device time, spec 6.1.5). */
  readonly occurredAt: EpochSeconds;
  /** IANA time zone of the place of work. */
  readonly timeZoneId: string;
  /** Identifier of the event this correction replaces, if any. */
  readonly supersedesEventId?: string | null;
  /** Project, if any. */
  readonly projectId?: string | null;
  /** Declared kind of a break start; missing means unclassified (ADR-0092). */
  readonly breakQualification?: BreakQualification | null;
}

/** Cancellation of an event. */
export interface VoidRecord {
  /** Identifier of the cancellation. */
  readonly id: string;
  /** Identifier of the cancelled event. */
  readonly targetEventId: string;
}

/** Kind of a work block. */
export type WorkBlockKind = "Work" | "Break";

/** A continuous work or break block. */
export interface WorkBlock {
  readonly kind: WorkBlockKind;
  readonly start: EpochSeconds;
  readonly end: EpochSeconds;
  /** `true` for the running block of an open shift. */
  readonly isOpen: boolean;
  readonly projectId: string | null;
  /** Event that started the block. */
  readonly startEventId: string;
  /** Event that ended the block, `null` while open. */
  readonly endEventId: string | null;
  /** Kind of a break block as declared with its break start; `Unclassified` for work blocks. */
  readonly breakQualification: BreakQualification;
}

/** A shift from clock-in to clock-out (or `asOf` if still open). */
export interface Shift {
  readonly start: EpochSeconds;
  readonly end: EpochSeconds;
}

/** A working day: all shifts that start on the same local date. */
export interface WorkDay {
  readonly date: LocalDate;
  readonly timeZoneId: string;
  readonly shifts: readonly Shift[];
  readonly blocks: readonly WorkBlock[];
  readonly workSeconds: number;
  readonly breakSeconds: number;
  readonly isClosed: boolean;
  readonly firstStart: EpochSeconds;
  readonly lastEnd: EpochSeconds;
  /** End of the last shift if the day is closed, otherwise `null`. */
  readonly closedAt: EpochSeconds | null;
}

/** Error codes of the projection. */
export const projectionErrorCodes = {
  duplicateIdentifier: "TT-1012",
} as const;

/**
 * Orders events by instant and, for equal instants, by identifier (UUID byte order equals the
 * order of the lowercase canonical text).
 *
 * @param left - First event.
 * @param right - Second event.
 * @returns Negative, zero or positive.
 */
export function compareChronologically(left: ClockEvent, right: ClockEvent): number {
  const byTime = left.occurredAt - right.occurredAt;

  if (byTime !== 0) {
    return byTime;
  }

  const byId = left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
  return byId;
}

/**
 * Selects the effective events: without voided and superseded ones, in chronological order.
 *
 * @param events - All events of one employee.
 * @param voids - Cancellations.
 * @returns The ordered effective events or an error for duplicate identifiers.
 */
export function selectEffectiveEvents(
  events: readonly ClockEvent[],
  voids: readonly VoidRecord[],
): Result<ClockEvent[]> {
  const seen = new Set<string>();

  for (const event of events) {
    if (seen.has(event.id)) {
      const duplicate = failure<ClockEvent[]>(
        projectionErrorCodes.duplicateIdentifier,
        `The identifier ${event.id} occurs more than once.`,
      );
      return duplicate;
    }

    seen.add(event.id);
  }

  const excluded = new Set(voids.map((record) => record.targetEventId));

  for (const event of events) {
    if (event.supersedesEventId !== undefined && event.supersedesEventId !== null) {
      excluded.add(event.supersedesEventId);
    }
  }

  const effective = events.filter((event) => !excluded.has(event.id)).sort(compareChronologically);
  const result = success(effective);

  return result;
}

interface MutableShift {
  readonly date: LocalDate;
  readonly timeZoneId: string;
  readonly start: EpochSeconds;
  readonly shiftProjectId: string | null;
  readonly blocks: WorkBlock[];
  end: EpochSeconds;
  isClosed: boolean;
  segmentKind: WorkBlockKind;
  segmentStart: EpochSeconds;
  segmentProjectId: string | null;
  segmentStartEventId: string;
  segmentQualification: BreakQualification;
}

interface MutableDay {
  readonly date: LocalDate;
  readonly timeZoneId: string;
  readonly shifts: Shift[];
  readonly blocks: WorkBlock[];
  allClosed: boolean;
}

/**
 * Closes the running segment of a shift; zero-length blocks are omitted.
 *
 * @param shift - The shift.
 * @param end - End of the segment.
 * @param isOpen - Whether the segment is still running.
 * @param endEventId - Event that ended the segment, `null` while open.
 */
function closeSegment(
  shift: MutableShift,
  end: EpochSeconds,
  isOpen: boolean,
  endEventId: string | null,
): void {
  if (end - shift.segmentStart > 0) {
    shift.blocks.push({
      kind: shift.segmentKind,
      start: shift.segmentStart,
      end,
      isOpen,
      projectId: shift.segmentKind === "Work" ? shift.segmentProjectId : null,
      startEventId: shift.segmentStartEventId,
      endEventId,
      breakQualification: shift.segmentQualification,
    });
  }
}

/**
 * Applies a non-clock-in event to a running shift.
 *
 * @param shift - The shift.
 * @param event - The event.
 */
function applyToShift(shift: MutableShift, event: ClockEvent): void {
  closeSegment(shift, event.occurredAt, false, event.id);

  if (event.kind === "BreakStart") {
    shift.segmentKind = "Break";
    shift.segmentStart = event.occurredAt;
    shift.segmentProjectId = null;
    shift.segmentStartEventId = event.id;
    shift.segmentQualification = event.breakQualification ?? "Unclassified";
  } else if (event.kind === "BreakEnd") {
    shift.segmentKind = "Work";
    shift.segmentStart = event.occurredAt;
    shift.segmentProjectId = event.projectId ?? shift.shiftProjectId;
    shift.segmentStartEventId = event.id;
    shift.segmentQualification = "Unclassified";
  } else {
    shift.end = event.occurredAt;
    shift.isClosed = true;
  }
}

/**
 * Adds a finished (or cut) shift to its working day.
 *
 * @param days - Days by date.
 * @param shift - The shift.
 */
function addShift(days: Map<LocalDate, MutableDay>, shift: MutableShift): void {
  let day = days.get(shift.date);

  if (day === undefined) {
    day = {
      date: shift.date,
      timeZoneId: shift.timeZoneId,
      shifts: [],
      blocks: [],
      allClosed: true,
    };
    days.set(shift.date, day);
  }

  day.shifts.push({ start: shift.start, end: shift.end });
  day.blocks.push(...shift.blocks);
  day.allClosed = day.allClosed && shift.isClosed;
}

/**
 * Builds the immutable working day with its sums.
 *
 * @param day - The accumulated day.
 * @returns The working day.
 */
function buildDay(day: MutableDay): WorkDay {
  let workSeconds = 0;
  let breakSeconds = 0;

  for (const block of day.blocks) {
    const duration = block.end - block.start;

    if (block.kind === "Work") {
      workSeconds += duration;
    } else {
      breakSeconds += duration;
    }
  }

  const firstShift = day.shifts[0];
  const lastShift = day.shifts[day.shifts.length - 1];

  if (firstShift === undefined || lastShift === undefined) {
    throw new Error("A working day always has at least one shift.");
  }

  const workDay: WorkDay = {
    date: day.date,
    timeZoneId: day.timeZoneId,
    shifts: day.shifts,
    blocks: day.blocks,
    workSeconds,
    breakSeconds,
    isClosed: day.allClosed,
    firstStart: firstShift.start,
    lastEnd: lastShift.end,
    closedAt: day.allClosed ? lastShift.end : null,
  };

  return workDay;
}

/**
 * Projects clock events into working days (ADR-0007): shifts belong to the local date of their
 * clock-in, open shifts are cut at `asOf`, voided and superseded events and events after `asOf` are
 * ignored (F-99; an event at `asOf` counts).
 * Complexity: O(n log n) for sorting, then O(n).
 *
 * @param events - All events of one employee.
 * @param voids - Cancellations.
 * @param asOf - Evaluation instant: open shifts end there, later events are ignored.
 * @returns Working days in date order or the first invalid transition.
 */
export function projectWorkDays(
  events: readonly ClockEvent[],
  voids: readonly VoidRecord[],
  asOf: EpochSeconds,
): Result<WorkDay[]> {
  const effective = selectEffectiveEvents(events, voids);

  if (!effective.ok) {
    return effective;
  }

  const days = new Map<LocalDate, MutableDay>();
  let state: ClockState = "Off";
  let shift: MutableShift | null = null;

  for (const event of effective.value) {
    const next = applyClockEvent(state, event.kind);

    if (!next.ok) {
      const rejected = failure<WorkDay[]>(
        next.error.code,
        `${next.error.message} (event ${event.id})`,
      );
      return rejected;
    }

    state = next.value;

    // Events after `asOf` have not happened yet at the evaluation instant (F-99); their
    // transitions are still checked.
    if (event.occurredAt > asOf) {
      continue;
    }

    if (event.kind === "ClockIn") {
      shift = {
        date: toLocalDate(event.occurredAt, event.timeZoneId),
        timeZoneId: event.timeZoneId,
        start: event.occurredAt,
        shiftProjectId: event.projectId ?? null,
        blocks: [],
        end: event.occurredAt,
        isClosed: false,
        segmentKind: "Work",
        segmentStart: event.occurredAt,
        segmentProjectId: event.projectId ?? null,
        segmentStartEventId: event.id,
        segmentQualification: "Unclassified",
      };
    } else if (shift !== null) {
      applyToShift(shift, event);

      if (event.kind === "ClockOut") {
        addShift(days, shift);
        shift = null;
      }
    }
  }

  if (shift !== null) {
    const end = Math.max(asOf, shift.segmentStart);
    closeSegment(shift, end, true, null);
    shift.end = end;
    addShift(days, shift);
  }

  const workDays = [...days.values()]
    .sort((left, right) => (left.date < right.date ? -1 : left.date > right.date ? 1 : 0))
    .map(buildDay);
  const projected = success(workDays);

  return projected;
}

/**
 * Replays events to the resulting clock state.
 *
 * @param orderedEvents - Effective events in chronological order.
 * @returns The final state or the first invalid transition.
 */
export function replayClockState(orderedEvents: readonly ClockEvent[]): Result<ClockState> {
  let state: ClockState = "Off";

  for (const event of orderedEvents) {
    const next = applyClockEvent(state, event.kind);

    if (!next.ok) {
      return next;
    }

    state = next.value;
  }

  const result = success(state);
  return result;
}
